import React, { useEffect, useRef, useState } from 'react';
import { fetchDashboardOrderSummary } from '../services/dataApi';
import PageLoader from '../components/PageLoader';
import {
  CalendarPicker,
  formatDateInput,
  formatSelectedDate,
  getBrowserTimezone,
  getTimezoneOffsetLabel,
  getTimezoneOptions,
  TimePicker,
  TimezonePicker,
  toRangeBoundary
} from '../components/OrderTools';
import './Page.css';
import './admin/AdminDashboard.css';

const emptyOrderSummary = {
  payin: { all: 0, completed: 0, allCount: 0, completedCount: 0 },
  payout: { all: 0, completed: 0, allCount: 0, completedCount: 0 }
};

const formatAmount = (value) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatOrderCount = (value) => {
  const count = Number(value || 0);
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'order' : 'orders'}`;
};

const formatPercentage = (part, total) => {
  const numerator = Number(part || 0);
  const denominator = Number(total || 0);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return '0%';
  }

  return `${(numerator / denominator * 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  })}%`;
};

const OrderSummaryCard = ({ title, summary }) => (
  <section className="dashboard-card dashboard-order-card">
    <div className="dashboard-card-header">
      <h3>{title}</h3>
      <span className="dashboard-card-meta">USDT</span>
    </div>
    <div className="dashboard-order-primary">
      <span className="dashboard-balance-label">Completed</span>
      <strong>{formatAmount(summary.completed)} USDT</strong>
      <span className="dashboard-order-count">{formatOrderCount(summary.completedCount)}</span>
    </div>
    <div className="dashboard-order-metrics">
      <div className="dashboard-order-metric">
        <span className="dashboard-rate-label">Total volume</span>
        <strong>{formatAmount(summary.all)} USDT</strong>
        <span className="dashboard-order-count">{formatOrderCount(summary.allCount)}</span>
      </div>
      <div className="dashboard-order-metric">
        <span className="dashboard-rate-label">Completed by count</span>
        <strong>{formatPercentage(summary.completedCount, summary.allCount)}</strong>
      </div>
      <div className="dashboard-order-metric">
        <span className="dashboard-rate-label">Completed by volume</span>
        <strong>{formatPercentage(summary.completed, summary.all)}</strong>
      </div>
    </div>
  </section>
);

const DashboardDateRange = ({
  dateFrom,
  dateTo,
  dateFromTime,
  dateToTime,
  timezone,
  onDateFromChange,
  onDateToChange,
  onDateFromTimeChange,
  onDateToTimeChange,
  onTimezoneChange
}) => {
  const [activePicker, setActivePicker] = useState(null);
  const rangeRef = useRef(null);
  const today = formatDateInput(new Date());
  const timezoneOptions = getTimezoneOptions();
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = formatDateInput(yesterdayDate);
  const monthStart = formatDateInput(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  useEffect(() => {
    if (!activePicker) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rangeRef.current?.contains(event.target)) setActivePicker(null);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [activePicker]);

  const changeDate = (target, value) => {
    if (target === 'from') {
      onDateFromChange(value);
      if (dateTo && value > dateTo) onDateToChange(value);
    } else {
      onDateToChange(value);
      if (dateFrom && value < dateFrom) onDateFromChange(value);
    }
  };

  const applyPreset = (from, to) => {
    onDateFromChange(from);
    onDateToChange(to);
    onDateFromTimeChange('00:00:00');
    onDateToTimeChange('23:59:59');
    setActivePicker(null);
  };

  const isFullDayRange = dateFromTime === '00:00:00' && dateToTime === '23:59:59';
  const activePreset = isFullDayRange && dateFrom === today && dateTo === today
    ? 'today'
    : isFullDayRange && dateFrom === yesterday && dateTo === yesterday
      ? 'yesterday'
      : isFullDayRange && dateFrom === monthStart && dateTo === today ? 'month' : null;

  const renderPicker = (target) => {
    if (!activePicker || activePicker.target !== target) return null;
    const isFrom = target === 'from';
    return (
      <div className="dashboard-date-popover">
        {activePicker.type === 'date' && <CalendarPicker value={isFrom ? dateFrom : dateTo} max={today} onChange={(value) => changeDate(target, value)} onClose={() => setActivePicker(null)} />}
        {activePicker.type === 'time' && <TimePicker value={isFrom ? dateFromTime : dateToTime} onChange={isFrom ? onDateFromTimeChange : onDateToTimeChange} onClose={() => setActivePicker(null)} />}
        {activePicker.type === 'timezone' && <TimezonePicker value={timezone} options={timezoneOptions} onChange={onTimezoneChange} onClose={() => setActivePicker(null)} />}
      </div>
    );
  };

  const renderField = (target, label, date, time, clear) => (
    <div className="dashboard-range-field">
      <span className="dashboard-range-label">{label}</span>
      <div className="dashboard-range-values">
        <button type="button" onClick={() => setActivePicker({ type: 'date', target })}>{date ? formatSelectedDate(date) : 'Choose date'}</button>
        {date && <><button type="button" onClick={() => setActivePicker({ type: 'time', target })}>{time}</button><button type="button" onClick={() => setActivePicker({ type: 'timezone', target })}>{getTimezoneOffsetLabel(timezone)}</button></>}
      </div>
      {date && <button type="button" className="dashboard-range-clear" onClick={clear} aria-label={`Clear ${label.toLowerCase()}`}>×</button>}
      {renderPicker(target)}
    </div>
  );

  return (
    <div className="dashboard-date-range" ref={rangeRef}>
      <div className="dashboard-range-presets">
        <button type="button" className={activePreset === 'today' ? 'is-active' : ''} onClick={() => applyPreset(today, today)}>Today</button>
        <button type="button" className={activePreset === 'yesterday' ? 'is-active' : ''} onClick={() => applyPreset(yesterday, yesterday)}>Yesterday</button>
        <button type="button" className={activePreset === 'month' ? 'is-active' : ''} onClick={() => applyPreset(monthStart, today)}>This month</button>
      </div>
      {renderField('from', 'Date from', dateFrom, dateFromTime, () => onDateFromChange(''))}
      {renderField('to', 'Date to', dateTo, dateToTime, () => onDateToChange(''))}
    </div>
  );
};

const TraderDashboard = () => {
  const [orderSummary, setOrderSummary] = useState(emptyOrderSummary);
  const [summaryDateLabel, setSummaryDateLabel] = useState('');
  const [dateFrom, setDateFrom] = useState(() => formatDateInput(new Date()));
  const [dateTo, setDateTo] = useState(() => formatDateInput(new Date()));
  const [dateFromTime, setDateFromTime] = useState('00:00:00');
  const [dateToTime, setDateToTime] = useState('23:59:59');
  const [dateTimezone, setDateTimezone] = useState(getBrowserTimezone);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => {
    let active = true;
    const loadOrderSummary = async () => {
      if (!dateFrom || !dateTo) {
        setOrderSummary(emptyOrderSummary);
        setSummaryDateLabel('');
        setSummaryError(null);
        setSummaryLoading(false);
        return;
      }

      const rangeFrom = toRangeBoundary(dateFrom, false, dateTimezone, dateFromTime);
      const rangeTo = toRangeBoundary(dateTo, true, dateTimezone, dateToTime);
      if (!rangeFrom || !rangeTo || Date.parse(rangeFrom) > Date.parse(rangeTo)) {
        setSummaryError('Date from must be earlier than date to');
        setSummaryLoading(false);
        return;
      }

      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const ordersPayload = await fetchDashboardOrderSummary(rangeFrom, rangeTo);
        if (active) {
          setOrderSummary(ordersPayload?.data || emptyOrderSummary);
          setSummaryDateLabel(`${formatSelectedDate(dateFrom)} — ${formatSelectedDate(dateTo)}`);
        }
      } catch (err) {
        if (active) setSummaryError(err.message || 'Failed to load dashboard order summary');
      } finally {
        if (active) setSummaryLoading(false);
      }
    };
    loadOrderSummary();
    return () => { active = false; };
  }, [dateFrom, dateTo, dateFromTime, dateToTime, dateTimezone]);

  return (
    <div className="page dashboard-page">
      <div className="page-header dashboard-page-header"><div className="page-heading"><span className="dashboard-eyebrow">Operations</span><h1>Dashboard</h1><p>Orders, balances and liquidity.</p></div></div>
      <div className="page-content dashboard-page-content">
        <PageLoader loading={summaryLoading && !summaryDateLabel}>
          <div className="page-data">
            <div className="page-section-header dashboard-order-section-header">
              <div className="page-section-title dashboard-section-title"><span className="dashboard-eyebrow">Overview</span><h2>Order performance</h2><p>Local time · {summaryDateLabel}</p></div>
              <DashboardDateRange dateFrom={dateFrom} dateTo={dateTo} dateFromTime={dateFromTime} dateToTime={dateToTime} timezone={dateTimezone} onDateFromChange={setDateFrom} onDateToChange={setDateTo} onDateFromTimeChange={setDateFromTime} onDateToTimeChange={setDateToTime} onTimezoneChange={setDateTimezone} />
            </div>
            {summaryError && <div className="dashboard-summary-error">{summaryError}</div>}
            <div className={`dashboard-grid dashboard-orders-grid${summaryLoading ? ' is-loading' : ''}`}>
              <OrderSummaryCard title="PayIN" summary={orderSummary.payin} />
              <OrderSummaryCard title="PayOUT" summary={orderSummary.payout} />
            </div>
          </div>
        </PageLoader>
      </div>
    </div>
  );
};

export default TraderDashboard;
